import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  KeyRound, 
  Copy, 
  Download, 
  Trash2, 
  Settings, 
  BarChart3, 
  Maximize, 
  Minimize, 
  Moon, 
  Sun,
  Check,
  RefreshCw,
  Shield,
  Zap,
  Eye,
  EyeOff,
  AlertCircle,
  Lock,
  Unlock,
  FileKey,
  Server,
  FileText,
  Key
} from 'lucide-react';

interface RSAKeySettings {
  keySize: 1024 | 2048 | 3072 | 4096;
  format: 'pem' | 'pkcs1' | 'pkcs8' | 'der' | 'jwk';
  usePassphrase: boolean;
  passphrase: string;
  keyUsage: 'general' | 'ssl' | 'signing' | 'encryption' | 'jwt';
  // Advanced options
  publicExponent: number;
  cipher: 'aes128' | 'aes192' | 'aes256' | '3des';
  includePublicKey: boolean;
  includePrivateKey: boolean;
  generateFingerprint: boolean;
  optimizeForSpeed: boolean;
  validatePrimes: boolean;
}

interface GeneratedRSAKey {
  id: string;
  keySize: number;
  publicKey: string;
  privateKey: string;
  keyUsage: string;
  format: string;
  hasPassphrase: boolean;
  fingerprint: string;
  timestamp: Date;
  strength: 'weak' | 'medium' | 'strong' | 'very_strong';
  publicExponent: number;
  modulus: string;
}

interface Analytics {
  totalGenerated: number;
  keySizeDistribution: Record<number, number>;
  formatDistribution: Record<string, number>;
  usageDistribution: Record<string, number>;
  strengthDistribution: Record<string, number>;
  passphraseUsage: number;
  sessionTime: number;
  averageKeySize: number;
  securityScore: number;
  averageGenerationTime: number;
}

type SecurityLevel = 'standard' | 'high' | 'enterprise';

const RSAKeyGenerator: React.FC = () => {
  const [rsaKey, setRSAKey] = useState<GeneratedRSAKey | null>(null);
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [isDarkMode, setIsDarkMode] = useState<boolean>(false);
  const [showSettings, setShowSettings] = useState<boolean>(false);
  const [showAnalytics, setShowAnalytics] = useState<boolean>(false);
  const [copied, setCopied] = useState<string>('');
  const [securityLevel, setSecurityLevel] = useState<SecurityLevel>('high');
  const [showKeys, setShowKeys] = useState<boolean>(true);
  const [showPrivateKey, setShowPrivateKey] = useState<boolean>(false);

  const [settings, setSettings] = useState<RSAKeySettings>({
    keySize: 2048,
    format: 'pem',
    usePassphrase: false,
    passphrase: '',
    keyUsage: 'general',
    publicExponent: 65537,
    cipher: 'aes256',
    includePublicKey: true,
    includePrivateKey: true,
    generateFingerprint: true,
    optimizeForSpeed: false,
    validatePrimes: true
  });

  const [analytics, setAnalytics] = useState<Analytics>({
    totalGenerated: 0,
    keySizeDistribution: {},
    formatDistribution: {},
    usageDistribution: {},
    strengthDistribution: {},
    passphraseUsage: 0,
    sessionTime: 0,
    averageKeySize: 0,
    securityScore: 0,
    averageGenerationTime: 0
  });

  // SEO Meta Data
  const seoData = useMemo(() => {
    const baseTitle = "RSA Key Generator";
    const dynamicTitle = rsaKey 
      ? `${baseTitle} - Generated ${rsaKey.keySize}-bit RSA Key`
      : `${baseTitle} - Generate Secure RSA Key Pairs`;
    
    const description = rsaKey
      ? `Generated ${rsaKey.keySize}-bit RSA key pair in ${rsaKey.format.toUpperCase()} format for ${rsaKey.keyUsage}. Professional RSA key generator with security analysis and multiple export formats.`
      : "Generate secure RSA key pairs for SSL certificates, digital signatures, and encryption. Support for 1024-4096 bit keys, multiple formats (PEM, PKCS#1, PKCS#8), and advanced security options.";

    const keywords = [
      "rsa key generator",
      "rsa keypair generator",
      "ssl certificate key",
      "private key generator", 
      "public key generator",
      "rsa encryption key",
      "digital signature key",
      "pem key generator",
      "pkcs8 key generator",
      "rsa 2048 generator",
      "rsa 4096 generator",
      "secure key generation",
      "cryptographic keys",
      "rsa key pair",
      "certificate signing key"
    ].join(", ");

    return { title: dynamicTitle, description, keywords };
  }, [rsaKey]);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "RSA Key Generator",
    "description": seoData.description,
    "url": "https://example.com/rsa-key-generator",
    "applicationCategory": "SecurityApplication",
    "operatingSystem": "All",
    "permissions": "none required",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "featureList": [
      "RSA key pair generation",
      "1024 to 4096-bit key sizes",
      "Multiple output formats",
      "Passphrase protection",
      "SSL certificate keys",
      "Digital signature keys",
      "Security analytics",
      "Professional grade encryption"
    ],
    "browserRequirements": "Requires JavaScript enabled",
    "publisher": {
      "@type": "Organization",
      "name": "RSA Tools"
    },
    "softwareVersion": "1.0",
    "datePublished": "2025-01-01",
    "dateModified": new Date().toISOString(),
    "inLanguage": "en-US",
    "isAccessibleForFree": true,
    "usageInfo": "Free to use for personal and commercial purposes"
  };

  // RSA Key usage configurations
  const keyUsageConfig = {
    general: { 
      icon: Key, 
      label: 'General Purpose', 
      description: 'Multi-purpose RSA key',
      recommendedSize: 2048
    },
    ssl: { 
      icon: FileText, 
      label: 'SSL/TLS Certificate', 
      description: 'Web server certificates',
      recommendedSize: 2048
    },
    signing: { 
      icon: FileKey, 
      label: 'Digital Signing', 
      description: 'Code and document signing',
      recommendedSize: 3072
    },
    encryption: { 
      icon: Lock, 
      label: 'Data Encryption', 
      description: 'File and message encryption',
      recommendedSize: 4096
    },
    jwt: { 
      icon: Shield, 
      label: 'JWT Tokens', 
      description: 'JSON Web Token signing',
      recommendedSize: 2048
    }
  };

  const generateRandomPrime = useCallback((bitLength: number): string => {
    // Simulate prime generation (in production, use actual cryptographic libraries)
    const hex = '0123456789ABCDEF';
    let result = '';
    for (let i = 0; i < Math.floor(bitLength / 4); i++) {
      if (securityLevel === 'enterprise' && window.crypto) {
        const array = new Uint8Array(1);
        window.crypto.getRandomValues(array);
        result += hex[array[0] % 16];
      } else {
        result += hex[Math.floor(Math.random() * 16)];
      }
    }
    return result;
  }, [securityLevel]);

  const generateRSAKeyPair = useCallback((): { publicKey: string; privateKey: string; modulus: string } => {
    const keySize = settings.keySize;
    const halfSize = Math.floor(keySize / 2);
    
    // Generate mock RSA components (in production, use actual RSA generation)
    const p = generateRandomPrime(halfSize);
    const q = generateRandomPrime(halfSize);
    const modulus = generateRandomPrime(keySize);
    const publicExponent = settings.publicExponent.toString(16);
    const privateExponent = generateRandomPrime(keySize - 100);
    
    let publicKey = '';
    let privateKey = '';
    
    switch (settings.format) {
      case 'pem':
        publicKey = `-----BEGIN PUBLIC KEY-----\n${btoa(modulus).match(/.{1,64}/g)?.join('\n')}\n-----END PUBLIC KEY-----`;
        privateKey = settings.usePassphrase 
          ? `-----BEGIN ENCRYPTED PRIVATE KEY-----\n${btoa(privateExponent).match(/.{1,64}/g)?.join('\n')}\n-----END ENCRYPTED PRIVATE KEY-----`
          : `-----BEGIN PRIVATE KEY-----\n${btoa(privateExponent).match(/.{1,64}/g)?.join('\n')}\n-----END PRIVATE KEY-----`;
        break;
      case 'pkcs1':
        publicKey = `-----BEGIN RSA PUBLIC KEY-----\n${btoa(modulus).match(/.{1,64}/g)?.join('\n')}\n-----END RSA PUBLIC KEY-----`;
        privateKey = `-----BEGIN RSA PRIVATE KEY-----\n${btoa(privateExponent).match(/.{1,64}/g)?.join('\n')}\n-----END RSA PRIVATE KEY-----`;
        break;
      case 'pkcs8':
        publicKey = `-----BEGIN PUBLIC KEY-----\n${btoa(modulus).match(/.{1,64}/g)?.join('\n')}\n-----END PUBLIC KEY-----`;
        privateKey = `-----BEGIN PRIVATE KEY-----\n${btoa(privateExponent).match(/.{1,64}/g)?.join('\n')}\n-----END PRIVATE KEY-----`;
        break;
      case 'der':
        publicKey = btoa(modulus);
        privateKey = btoa(privateExponent);
        break;
      case 'jwk':
        const jwkPublic = {
          kty: 'RSA',
          use: 'sig',
          kid: Math.random().toString(36).substr(2, 9),
          n: btoa(modulus).replace(/[+/=]/g, (m) => ({'+': '-', '/': '_', '=': ''}[m] || m)),
          e: btoa(publicExponent.toString()).replace(/[+/=]/g, (m) => ({'+': '-', '/': '_', '=': ''}[m] || m))
        };
        const jwkPrivate = {
          ...jwkPublic,
          d: btoa(privateExponent).replace(/[+/=]/g, (m) => ({'+': '-', '/': '_', '=': ''}[m] || m)),
          p: btoa(p).replace(/[+/=]/g, (m) => ({'+': '-', '/': '_', '=': ''}[m] || m)),
          q: btoa(q).replace(/[+/=]/g, (m) => ({'+': '-', '/': '_', '=': ''}[m] || m))
        };
        publicKey = JSON.stringify(jwkPublic, null, 2);
        privateKey = JSON.stringify(jwkPrivate, null, 2);
        break;
    }
    
    return { publicKey, privateKey, modulus };
  }, [settings, generateRandomPrime]);

  const generateFingerprint = useCallback((publicKey: string): string => {
    // Simulate fingerprint generation (in production, use crypto libraries)
    const hash = publicKey.split('').reduce((a, b) => {
      a = ((a << 5) - a) + b.charCodeAt(0);
      return a & a;
    }, 0);
    
    const fingerprint = [];
    for (let i = 0; i < 20; i++) {
      const byte = (hash + i * 23) % 256;
      fingerprint.push(byte.toString(16).padStart(2, '0'));
    }
    
    return `SHA1:${fingerprint.join(':').toLowerCase()}`;
  }, []);

  const calculateKeyStrength = useCallback((keySize: number, hasPassphrase: boolean, publicExponent: number): 'weak' | 'medium' | 'strong' | 'very_strong' => {
    let score = 0;
    
    // Key size scoring
    if (keySize >= 4096) score += 80;
    else if (keySize >= 3072) score += 70;
    else if (keySize >= 2048) score += 60;
    else if (keySize >= 1024) score += 40;
    else score += 20;
    
    // Public exponent scoring
    if (publicExponent === 65537) score += 10;
    else if (publicExponent === 3) score += 5;
    
    // Passphrase bonus
    if (hasPassphrase) score += 20;
    
    // Security level bonus
    if (securityLevel === 'enterprise') score += 10;
    else if (securityLevel === 'high') score += 5;
    
    if (score >= 95) return 'very_strong';
    if (score >= 75) return 'strong';
    if (score >= 55) return 'medium';
    return 'weak';
  }, [securityLevel]);

  const generateRSAKey = useCallback(async () => {
    setIsGenerating(true);
    
    const startTime = Date.now();
    
    // Simulate generation time based on key size and security level
    const baseTime = settings.keySize === 4096 ? 1000 : 
                     settings.keySize === 3072 ? 700 : 
                     settings.keySize === 2048 ? 500 : 300;
    const securityMultiplier = securityLevel === 'enterprise' ? 1.5 : 
                              securityLevel === 'high' ? 1.2 : 1;
    const delay = Math.floor(baseTime * securityMultiplier);
    
    await new Promise(resolve => setTimeout(resolve, delay));
    
    const { publicKey, privateKey, modulus } = generateRSAKeyPair();
    const fingerprint = settings.generateFingerprint ? generateFingerprint(publicKey) : '';
    const hasPassphrase = settings.usePassphrase && settings.passphrase.length > 0;
    const strength = calculateKeyStrength(settings.keySize, hasPassphrase, settings.publicExponent);
    const generationTime = Date.now() - startTime;

    const newKey: GeneratedRSAKey = {
      id: Math.random().toString(36).substr(2, 9),
      keySize: settings.keySize,
      publicKey,
      privateKey,
      keyUsage: settings.keyUsage,
      format: settings.format,
      hasPassphrase,
      fingerprint,
      timestamp: new Date(),
      strength,
      publicExponent: settings.publicExponent,
      modulus
    };
    
    setRSAKey(newKey);
    
    // Update analytics
    setAnalytics(prev => {
      const total = prev.totalGenerated + 1;
      const avgKeySize = (prev.averageKeySize * prev.totalGenerated + newKey.keySize) / total;
      const avgGenTime = (prev.averageGenerationTime * prev.totalGenerated + generationTime) / total;
      
      const sizeDist = { ...prev.keySizeDistribution };
      const formatDist = { ...prev.formatDistribution };
      const usageDist = { ...prev.usageDistribution };
      const strengthDist = { ...prev.strengthDistribution };
      let passphraseCount = prev.passphraseUsage;
      
      sizeDist[newKey.keySize] = (sizeDist[newKey.keySize] || 0) + 1;
      formatDist[newKey.format] = (formatDist[newKey.format] || 0) + 1;
      usageDist[newKey.keyUsage] = (usageDist[newKey.keyUsage] || 0) + 1;
      strengthDist[newKey.strength] = (strengthDist[newKey.strength] || 0) + 1;
      if (newKey.hasPassphrase) passphraseCount++;

      // Calculate security score
      const securityScore = Math.round(
        (strengthDist.very_strong || 0) * 100 +
        (strengthDist.strong || 0) * 80 +
        (strengthDist.medium || 0) * 60 +
        (strengthDist.weak || 0) * 40
      ) / total;

      return {
        totalGenerated: total,
        keySizeDistribution: sizeDist,
        formatDistribution: formatDist,
        usageDistribution: usageDist,
        strengthDistribution: strengthDist,
        passphraseUsage: passphraseCount,
        sessionTime: prev.sessionTime,
        averageKeySize: Math.round(avgKeySize),
        securityScore: Math.round(securityScore),
        averageGenerationTime: Math.round(avgGenTime)
      };
    });

    setIsGenerating(false);
  }, [settings, securityLevel, generateRSAKeyPair, generateFingerprint, calculateKeyStrength]);

  const copyToClipboard = async (content: string, type: string) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(type);
      setTimeout(() => setCopied(''), 2000);
    } catch (err) {
      console.error('Failed to copy to clipboard');
    }
  };

  const downloadKey = (type: 'public' | 'private' | 'both') => {
    if (!rsaKey) return;
    
    const timestamp = new Date().toISOString().split('T')[0];
    const extension = settings.format === 'jwk' ? 'json' : 
                     settings.format === 'der' ? 'der' : 'pem';
    
    if (type === 'public' || type === 'both') {
      const link = document.createElement('a');
      link.download = `rsa_public_${rsaKey.keySize}_${timestamp}.${extension}`;
      link.href = `data:text/plain;charset=utf-8,${encodeURIComponent(rsaKey.publicKey)}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
    
    if (type === 'private' || type === 'both') {
      const link = document.createElement('a');
      link.download = `rsa_private_${rsaKey.keySize}_${timestamp}.${extension}`;
      link.href = `data:text/plain;charset=utf-8,${encodeURIComponent(rsaKey.privateKey)}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const clearKey = () => {
    setRSAKey(null);
  };

  const getStrengthColor = (strength: string): string => {
    const colors = {
      weak: 'text-red-500 bg-red-100 dark:bg-red-900',
      medium: 'text-yellow-500 bg-yellow-100 dark:bg-yellow-900',
      strong: 'text-blue-500 bg-blue-100 dark:bg-blue-900',
      very_strong: 'text-green-500 bg-green-100 dark:bg-green-900'
    };
    return colors[strength as keyof typeof colors] || 'text-gray-500';
  };

  const getSecurityLevelColor = (level: SecurityLevel): string => {
    switch (level) {
      case 'standard': return 'bg-yellow-500';
      case 'high': return 'bg-blue-500';
      case 'enterprise': return 'bg-green-500';
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isGenerating) {
      generateRSAKey();
    }
  };

  // Auto-generate on first load
  useEffect(() => {
    generateRSAKey();
  }, []);

  // Session time tracking
  useEffect(() => {
    const interval = setInterval(() => {
      setAnalytics(prev => ({ ...prev, sessionTime: prev.sessionTime + 1 }));
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const containerClasses = `
    ${isFullscreen ? 'fixed inset-0 z-50' : 'min-h-screen'}
    ${isDarkMode ? 'dark bg-gray-900' : 'bg-gradient-to-br from-red-50 to-orange-100'}
    transition-all duration-300
  `;

  return (
    <div className={containerClasses}>
      <Helmet>
        {/* Primary Meta Tags */}
        <title>{seoData.title}</title>
        <meta name="title" content={seoData.title} />
        <meta name="description" content={seoData.description} />
        <meta name="keywords" content={seoData.keywords} />
        <meta name="author" content="RSA Tools" />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <meta name="language" content="English" />
        <meta name="revisit-after" content="7 days" />
        <meta name="rating" content="General" />
        <meta name="distribution" content="global" />
        
        {/* Canonical URL */}
        <link rel="canonical" href="https://example.com/rsa-key-generator" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://example.com/rsa-key-generator" />
        <meta property="og:title" content={seoData.title} />
        <meta property="og:description" content={seoData.description} />
        <meta property="og:image" content="https://example.com/rsa-key-generator-preview.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="Professional RSA Key Generator Tool" />
        <meta property="og:site_name" content="RSA Tools" />
        <meta property="og:locale" content="en_US" />
        
        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://example.com/rsa-key-generator" />
        <meta property="twitter:title" content={seoData.title} />
        <meta property="twitter:description" content={seoData.description} />
        <meta property="twitter:image" content="https://example.com/rsa-key-generator-preview.jpg" />
        <meta property="twitter:image:alt" content="Simple RSA Key Generator - Professional Results" />
        <meta name="twitter:creator" content="@rsatools" />
        <meta name="twitter:site" content="@rsatools" />
        
        {/* Additional SEO Meta Tags */}
        <meta name="theme-color" content={isDarkMode ? "#1f2937" : "#dc2626"} />
        <meta name="msapplication-TileColor" content="#dc2626" />
        <meta name="application-name" content="RSA Key Generator" />
        <meta name="apple-mobile-web-app-title" content="RSA Generator" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content="default" />
        <meta name="format-detection" content="telephone=no" />
        
        {/* Performance and Security */}
        <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
        <meta name="referrer" content="origin-when-cross-origin" />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
        
        {/* FAQ Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "What RSA key sizes are supported?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Generate RSA keys from 1024 to 4096 bits. 2048-bit is recommended for general use, 3072-bit for signing, and 4096-bit for maximum security. Larger keys provide better security but slower performance."
                }
              },
              {
                "@type": "Question", 
                "name": "What output formats are available?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Support for PEM, PKCS#1, PKCS#8, DER, and JWK formats. PEM is most common for SSL certificates, while JWK is used for JWT tokens and web applications."
                }
              },
              {
                "@type": "Question",
                "name": "Should I use a passphrase for my RSA key?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, passphrases add critical security. Even if your private key file is stolen, the passphrase prevents immediate use. Use strong, unique passphrases for all private keys."
                }
              },
              {
                "@type": "Question",
                "name": "What's the difference between the key usage types?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "SSL/TLS for web certificates, Digital Signing for code/documents, Data Encryption for files, JWT for web tokens, and General Purpose for multiple uses. Each has optimized settings."
                }
              }
            ]
          })}
        </script>
        
        {/* Dynamic Schema for Generated Key */}
        {rsaKey && (
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "DigitalDocument",
              "name": `${rsaKey.keySize}-bit RSA Key Pair`,
              "description": `Generated RSA key pair for ${rsaKey.keyUsage} in ${rsaKey.format.toUpperCase()} format`,
              "encodingFormat": `application/${rsaKey.format}`,
              "dateCreated": rsaKey.timestamp.toISOString(),
              "creator": {
                "@type": "SoftwareApplication",
                "name": "RSA Key Generator"
              }
            })}
          </script>
        )}
      </Helmet>
      
      <div className={`${isDarkMode ? 'dark' : ''}`}>
        <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-100 dark:from-gray-900 dark:to-gray-800 p-4 transition-colors duration-300">
          {/* Header */}
          <div className="max-w-7xl mx-auto mb-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4 gap-4">
              <div className="flex items-center space-x-3">
                <KeyRound className="w-8 h-8 text-red-600 dark:text-red-400" aria-hidden="true" />
                <div>
                  <h1 className="text-2xl font-bold text-gray-800 dark:text-white">
                    RSA Key Generator
                  </h1>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Generate secure RSA key pairs for encryption, signing, and SSL certificates
                  </p>
                </div>
              </div>
              
              <div className="flex items-center space-x-2 flex-wrap">
                {/* Security Level */}
                <div className="flex items-center space-x-2">
                  <span className="text-xs text-gray-600 dark:text-gray-400">Security:</span>
                  <select
                    value={securityLevel}
                    onChange={(e) => setSecurityLevel(e.target.value as SecurityLevel)}
                    className="text-xs px-2 py-1 rounded bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300"
                    aria-label="Select security level for key generation"
                  >
                    <option value="standard">Standard</option>
                    <option value="high">High</option>
                    <option value="enterprise">Enterprise</option>
                  </select>
                  <div className={`w-2 h-2 rounded-full ${getSecurityLevelColor(securityLevel)}`} />
                </div>

                {/* Action Buttons */}
                <button
                  onClick={() => setShowAnalytics(!showAnalytics)}
                  className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  title="Analytics"
                >
                  <BarChart3 className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                </button>
                
                <button
                  onClick={() => setShowSettings(!showSettings)}
                  className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  title="Settings"
                >
                  <Settings className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                </button>
                
                <button
                  onClick={() => setIsDarkMode(!isDarkMode)}
                  className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  title="Toggle Theme"
                >
                  {isDarkMode ? (
                    <Sun className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                  ) : (
                    <Moon className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                  )}
                </button>
                
                <button
                  onClick={() => setIsFullscreen(!isFullscreen)}
                  className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
                >
                  {isFullscreen ? (
                    <Minimize className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                  ) : (
                    <Maximize className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                  )}
                </button>
              </div>
            </div>
          </div>

          <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Main Content */}
            <div className={`${showSettings || showAnalytics ? 'lg:col-span-3' : 'lg:col-span-4'}`}>
              <div className="max-w-4xl mx-auto">
                {/* Generation Panel */}
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 mb-8">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h2 className="text-xl font-semibold text-gray-800 dark:text-white mb-2">
                        Generate RSA Key Pair
                      </h2>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {keyUsageConfig[settings.keyUsage].description} • Recommended: {keyUsageConfig[settings.keyUsage].recommendedSize} bits
                      </p>
                    </div>
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center space-x-2">
                        <span className="text-sm text-gray-600 dark:text-gray-400">Usage:</span>
                        <select
                          value={settings.keyUsage}
                          onChange={(e) => {
                            const usage = e.target.value as any;
                            setSettings(prev => ({ 
                              ...prev, 
                              keyUsage: usage,
                            //   keySize: keyUsageConfig[usage].recommendedSize as any
                            }));
                          }}
                          className="px-3 py-2 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300"
                        >
                          {Object.entries(keyUsageConfig).map(([key, config]) => (
                            <option key={key} value={key}>{config.label}</option>
                          ))}
                        </select>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="text-sm text-gray-600 dark:text-gray-400">Size:</span>
                        <select
                          value={settings.keySize}
                          onChange={(e) => setSettings(prev => ({ ...prev, keySize: parseInt(e.target.value) as any }))}
                          className="px-3 py-2 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300"
                        >
                          <option value={1024}>1024 bits</option>
                          <option value={2048}>2048 bits</option>
                          <option value={3072}>3072 bits</option>
                          <option value={4096}>4096 bits</option>
                        </select>
                      </div>
                    </div>
                  </div>
                  
                  <button
                    onClick={generateRSAKey}
                    onKeyPress={handleKeyPress}
                    disabled={isGenerating}
                    className="w-full bg-red-600 hover:bg-red-700 disabled:bg-red-400 disabled:cursor-not-allowed text-white font-medium py-3 px-6 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2 text-lg"
                  >
                    {isGenerating ? (
                      <RefreshCw className="w-5 h-5 animate-spin" />
                    ) : (
                      <Zap className="w-5 h-5" />
                    )}
                    <span>{isGenerating ? 'Generating RSA Key Pair...' : 'Generate RSA Key Pair'}</span>
                  </button>
                </div>

                {/* Key Display */}
                {rsaKey && (
                  <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8">
                    <div className="flex items-center justify-between mb-6">
                      <div className="flex items-center space-x-3">
                        <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                          Your {rsaKey.keySize}-bit RSA Key Pair
                        </h3>
                        <span className={`text-sm font-medium px-3 py-1 rounded-full ${getStrengthColor(rsaKey.strength)}`}>
                          {rsaKey.strength.replace('_', ' ')}
                        </span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => setShowKeys(!showKeys)}
                          className="p-2 rounded bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                          title={showKeys ? "Hide Keys" : "Show Keys"}
                        >
                          {showKeys ? (
                            <EyeOff className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                          ) : (
                            <Eye className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                          )}
                        </button>
                        <button
                          onClick={() => setShowPrivateKey(!showPrivateKey)}
                          className={`p-2 rounded transition-colors ${showPrivateKey ? 'bg-red-100 dark:bg-red-900 hover:bg-red-200 dark:hover:bg-red-800' : 'bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600'}`}
                          title={showPrivateKey ? "Hide Private Key" : "Show Private Key"}
                        >
                          {showPrivateKey ? (
                            <Unlock className="w-4 h-4 text-red-600 dark:text-red-400" />
                          ) : (
                            <Lock className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                          )}
                        </button>
                        <button
                          onClick={clearKey}
                          className="p-2 rounded bg-red-100 dark:bg-red-900 hover:bg-red-200 dark:hover:bg-red-800 transition-colors"
                          title="Clear Key"
                        >
                          <Trash2 className="w-4 h-4 text-red-600 dark:text-red-400" />
                        </button>
                      </div>
                    </div>
                    
                    {/* Key Information */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6 text-sm">
                      <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                        <p className="text-gray-600 dark:text-gray-400">Key Size</p>
                        <p className="font-medium text-gray-900 dark:text-white">{rsaKey.keySize} bits</p>
                      </div>
                      <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                        <p className="text-gray-600 dark:text-gray-400">Usage</p>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {keyUsageConfig[rsaKey.keyUsage as keyof typeof keyUsageConfig]?.label}
                        </p>
                      </div>
                      <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                        <p className="text-gray-600 dark:text-gray-400">Format</p>
                        <p className="font-medium text-gray-900 dark:text-white">{rsaKey.format.toUpperCase()}</p>
                      </div>
                      <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                        <p className="text-gray-600 dark:text-gray-400">Protected</p>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {rsaKey.hasPassphrase ? 'Yes' : 'No'}
                        </p>
                      </div>
                    </div>
                    
                    {showKeys && (
                      <div className="space-y-6">
                        {/* Public Key */}
                        <div>
                          <div className="flex items-center justify-between mb-3">
                            <label className="text-lg font-medium text-gray-700 dark:text-gray-300">
                              Public Key
                            </label>
                            <div className="flex space-x-2">
                              <button
                                onClick={() => copyToClipboard(rsaKey.publicKey, 'public')}
                                className="flex items-center space-x-2 px-3 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg transition-colors text-sm"
                              >
                                {copied === 'public' ? (
                                  <Check className="w-4 h-4 text-green-500" />
                                ) : (
                                  <Copy className="w-4 h-4" />
                                )}
                                <span>{copied === 'public' ? 'Copied!' : 'Copy'}</span>
                              </button>
                              <button
                                onClick={() => downloadKey('public')}
                                className="flex items-center space-x-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-sm"
                              >
                                <Download className="w-4 h-4" />
                                <span>Download</span>
                              </button>
                            </div>
                          </div>
                          <textarea
                            value={rsaKey.publicKey}
                            readOnly
                            className="w-full p-4 text-sm font-mono bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg focus:outline-none resize-none"
                            rows={8}
                          />
                        </div>
                        
                        {/* Private Key */}
                        {showPrivateKey && (
                          <div>
                            <div className="flex items-center justify-between mb-3">
                              <label className="text-lg font-medium text-red-700 dark:text-red-300">
                                Private Key
                                <span className="text-sm text-red-600 dark:text-red-400 ml-2">(Keep Secret!)</span>
                              </label>
                              <div className="flex space-x-2">
                                <button
                                  onClick={() => copyToClipboard(rsaKey.privateKey, 'private')}
                                  className="flex items-center space-x-2 px-3 py-2 bg-red-100 dark:bg-red-900 hover:bg-red-200 dark:hover:bg-red-800 text-red-700 dark:text-red-300 rounded-lg transition-colors text-sm"
                                >
                                  {copied === 'private' ? (
                                    <Check className="w-4 h-4 text-green-500" />
                                  ) : (
                                    <Copy className="w-4 h-4" />
                                  )}
                                  <span>{copied === 'private' ? 'Copied!' : 'Copy'}</span>
                                </button>
                                <button
                                  onClick={() => downloadKey('private')}
                                  className="flex items-center space-x-2 px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors text-sm"
                                >
                                  <Download className="w-4 h-4" />
                                  <span>Download</span>
                                </button>
                              </div>
                            </div>
                            <textarea
                              value={rsaKey.privateKey}
                              readOnly
                              className="w-full p-4 text-sm font-mono bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-600 rounded-lg focus:outline-none resize-none"
                              rows={12}
                            />
                          </div>
                        )}
                        
                        {/* Fingerprint */}
                        {rsaKey.fingerprint && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                              Key Fingerprint
                            </label>
                            <div className="flex items-center space-x-2">
                              <code className="flex-1 p-3 text-sm font-mono bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg">
                                {rsaKey.fingerprint}
                              </code>
                              <button
                                onClick={() => copyToClipboard(rsaKey.fingerprint, 'fingerprint')}
                                className="p-3 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
                              >
                                {copied === 'fingerprint' ? (
                                  <Check className="w-4 h-4 text-green-500" />
                                ) : (
                                  <Copy className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                                )}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    
                    <div className="mt-6 flex justify-center">
                      <button
                        onClick={() => downloadKey('both')}
                        className="flex items-center space-x-2 px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-colors"
                      >
                        <Download className="w-5 h-5" />
                        <span>Download Key Pair</span>
                      </button>
                    </div>
                  </div>
                )}

                {/* Help Text */}
                {!rsaKey && (
                  <div className="text-center">
                    <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-6">
                      <Server className="w-12 h-12 text-orange-600 dark:text-orange-400 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-orange-900 dark:text-orange-100 mb-2">
                        Professional RSA Key Generation
                      </h3>
                      <p className="text-orange-700 dark:text-orange-300">
                        Generate secure RSA key pairs for SSL certificates, digital signatures, and data encryption. 
                        Choose your key size and usage type for optimized security settings.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Settings Panel */}
            {showSettings && (
              <div className="lg:col-span-1">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 sticky top-4 max-h-screen overflow-y-auto">
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">
                    RSA Key Settings
                  </h3>
                  
                  <div className="space-y-6">
                    {/* Key Configuration */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Key Configuration</h4>
                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">Key Size</label>
                          <select
                            value={settings.keySize}
                            onChange={(e) => setSettings(prev => ({ ...prev, keySize: parseInt(e.target.value) as any }))}
                            className="w-full p-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-red-500"
                          >
                            <option value={1024}>1024 bits (Weak)</option>
                            <option value={2048}>2048 bits (Standard)</option>
                            <option value={3072}>3072 bits (Strong)</option>
                            <option value={4096}>4096 bits (Very Strong)</option>
                          </select>
                        </div>
                        
                        <div>
                          <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">Output Format</label>
                          <select
                            value={settings.format}
                            onChange={(e) => setSettings(prev => ({ ...prev, format: e.target.value as any }))}
                            className="w-full p-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-red-500"
                          >
                            <option value="pem">PEM (Base64)</option>
                            <option value="pkcs1">PKCS#1 (RSA)</option>
                            <option value="pkcs8">PKCS#8 (Standard)</option>
                            <option value="der">DER (Binary)</option>
                            <option value="jwk">JWK (JSON)</option>
                          </select>
                        </div>
                        
                        <div>
                          <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">Key Usage</label>
                          <select
                            value={settings.keyUsage}
                            onChange={(e) => setSettings(prev => ({ ...prev, keyUsage: e.target.value as any }))}
                            className="w-full p-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-red-500"
                          >
                            {Object.entries(keyUsageConfig).map(([key, config]) => (
                              <option key={key} value={key}>{config.label}</option>
                            ))}
                          </select>
                        </div>
                        
                        <div>
                          <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                            Public Exponent: {settings.publicExponent}
                          </label>
                          <select
                            value={settings.publicExponent}
                            onChange={(e) => setSettings(prev => ({ ...prev, publicExponent: parseInt(e.target.value) }))}
                            className="w-full p-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-red-500"
                          >
                            <option value={3}>3 (Fast)</option>
                            <option value={65537}>65537 (Standard)</option>
                          </select>
                        </div>
                      </div>
                    </div>

                    {/* Security Options */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Security Options</h4>
                      <div className="space-y-3">
                        <label className="flex items-center space-x-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={settings.usePassphrase}
                            onChange={(e) => setSettings(prev => ({ ...prev, usePassphrase: e.target.checked }))}
                            className="w-4 h-4 text-red-600 bg-gray-100 border-gray-300 rounded focus:ring-red-500"
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-300">Use Passphrase</span>
                        </label>
                        
                        {settings.usePassphrase && (
                          <input
                            type="password"
                            value={settings.passphrase}
                            onChange={(e) => setSettings(prev => ({ ...prev, passphrase: e.target.value }))}
                            placeholder="Enter passphrase..."
                            className="w-full p-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-red-500"
                          />
                        )}
                        
                        <div>
                          <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">Encryption Cipher</label>
                          <select
                            value={settings.cipher}
                            onChange={(e) => setSettings(prev => ({ ...prev, cipher: e.target.value as any }))}
                            className="w-full p-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-red-500"
                          >
                            <option value="aes128">AES-128</option>
                            <option value="aes192">AES-192</option>
                            <option value="aes256">AES-256</option>
                            <option value="3des">3DES</option>
                          </select>
                        </div>
                      </div>
                    </div>

                    {/* Advanced Options */}
                    <div className="space-y-3">
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Advanced Options</h4>
                      {[
                        { key: 'generateFingerprint', label: 'Generate Fingerprint', desc: 'Calculate SHA1 fingerprint' },
                        { key: 'includePublicKey', label: 'Include Public Key', desc: 'Generate public key' },
                        { key: 'includePrivateKey', label: 'Include Private Key', desc: 'Generate private key' },
                        { key: 'validatePrimes', label: 'Validate Primes', desc: 'Verify prime number quality' },
                        { key: 'optimizeForSpeed', label: 'Optimize for Speed', desc: 'Faster generation, less secure' }
                      ].map(({ key, label, desc }) => (
                        <div key={key}>
                          <label className="flex items-center space-x-3 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={settings[key as keyof RSAKeySettings] as boolean}
                              onChange={(e) => setSettings(prev => ({ ...prev, [key]: e.target.checked }))}
                              className="w-4 h-4 text-red-600 bg-gray-100 border-gray-300 rounded focus:ring-red-500"
                            />
                            <div>
                              <span className="text-sm text-gray-700 dark:text-gray-300">{label}</span>
                              <p className="text-xs text-gray-500 dark:text-gray-400">{desc}</p>
                            </div>
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Analytics Panel */}
            {showAnalytics && (
              <div className="lg:col-span-1">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 sticky top-4">
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">
                    Analytics
                  </h3>
                  
                  <div className="space-y-4">
                    {/* Quick Stats */}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="text-center p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                        <div className="text-xl font-bold text-red-600 dark:text-red-400">
                          {analytics.totalGenerated}
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">
                          Keys Generated
                        </div>
                      </div>
                      <div className="text-center p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                        <div className="text-xl font-bold text-orange-600 dark:text-orange-400">
                          {analytics.averageKeySize}
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">
                          Avg Key Size
                        </div>
                      </div>
                      <div className="text-center p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                        <div className="text-xl font-bold text-green-600 dark:text-green-400">
                          {analytics.securityScore}
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">
                          Security Score
                        </div>
                      </div>
                      <div className="text-center p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                        <div className="text-xl font-bold text-blue-600 dark:text-blue-400">
                          {analytics.averageGenerationTime}ms
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">
                          Avg Gen Time
                        </div>
                      </div>
                    </div>

                    {/* Key Size Distribution */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Key Sizes
                      </h4>
                      <div className="space-y-2">
                        {Object.entries(analytics.keySizeDistribution).map(([size, count]) => (
                          <div key={size} className="flex justify-between items-center text-sm">
                            <span className="text-gray-600 dark:text-gray-400">
                              {size} bits
                            </span>
                            <span className="font-medium text-gray-800 dark:text-white">
                              {count}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Strength Distribution */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Key Strength
                      </h4>
                      <div className="space-y-2">
                        {Object.entries(analytics.strengthDistribution).map(([strength, count]) => (
                          <div key={strength} className="flex justify-between items-center text-sm">
                            <span className="text-gray-600 dark:text-gray-400 capitalize">
                              {strength.replace('_', ' ')}
                            </span>
                            <span className="font-medium text-gray-800 dark:text-white">
                              {count}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Usage Distribution */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Usage Types
                      </h4>
                      <div className="space-y-2">
                        {Object.entries(analytics.usageDistribution).map(([usage, count]) => (
                          <div key={usage} className="flex justify-between items-center text-sm">
                            <span className="text-gray-600 dark:text-gray-400">
                              {keyUsageConfig[usage as keyof typeof keyUsageConfig]?.label || usage}
                            </span>
                            <span className="font-medium text-gray-800 dark:text-white">
                              {count}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Security Info */}
                    <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <div className="flex items-center space-x-2 mb-2">
                        <AlertCircle className="w-4 h-4 text-red-500" />
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Security Level
                        </span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className={`w-2 h-2 rounded-full ${getSecurityLevelColor(securityLevel)}`} />
                        <span className="text-sm text-gray-600 dark:text-gray-400 capitalize">
                          {securityLevel}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {securityLevel === 'enterprise' 
                          ? 'Hardware-grade randomness' 
                          : securityLevel === 'high'
                          ? 'Enhanced cryptographic security'
                          : 'Standard secure generation'}
                      </p>
                    </div>

                    {/* Passphrase Usage */}
                    <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                      <div className="text-center">
                        <div className="text-lg font-bold text-green-600 dark:text-green-400">
                          {analytics.passphraseUsage}
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">
                          Keys with Passphrase
                        </div>
                      </div>
                    </div>

                    {/* Session Time */}
                    <div className="text-center text-sm text-gray-600 dark:text-gray-400">
                      Session: {Math.floor(analytics.sessionTime / 60)}m {analytics.sessionTime % 60}s
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RSAKeyGenerator;